<script lang="ts">
  import { untrack } from "svelte"
  import type { ProgressStepContext } from "@kontextwerk/web-sdk/ui/navigation/ProgressSteps.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import LoadingWrapper from "@kontextwerk/web-sdk/ui/layout/LoadingWrapper.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import type { DemoDataResetMembershipCandidate } from "../../../../../../../../../../../../../../../api/kehrblatt/demo-data"
  import WorkflowActionBox from "../workflow-action-box"
  import WorkflowStepFrame from "../workflow-step-frame"
  import {
    memberOutputFromCandidate,
    type DemoResetMemberOutput,
    type DemoResetMemberRole,
  } from "../../_logic/demo-data-reset-contract"

  const {
    ctx,
    role,
    title,
    description,
    helpModalTextId,
    actionBoxLabel,
    roleDescription,
    emptyMessage,
    candidates,
    initialMembershipId = null,
    excludedMembershipId = null,
    loading = false,
    loadError = "",
    onRetry = null,
    output = null,
    onOutput,
    onEdit,
  }: {
    ctx: ProgressStepContext
    role: DemoResetMemberRole
    title: string
    description: string
    helpModalTextId: string
    actionBoxLabel: string
    roleDescription: string
    emptyMessage: string
    candidates: readonly DemoDataResetMembershipCandidate[]
    initialMembershipId?: string | null
    excludedMembershipId?: string | null
    loading?: boolean
    loadError?: string
    onRetry?: (() => void) | null
    output?: DemoResetMemberOutput | null
    onOutput: (output: DemoResetMemberOutput) => void
    onEdit: () => void
  } = $props()

  let selectedMembershipId = $state<string | null>(untrack(() => initialMembershipId))
  const available = $derived(
    candidates.filter((candidate) => candidate.membership_id !== excludedMembershipId),
  )
  const selected = $derived(
    available.find((candidate) => candidate.membership_id === selectedMembershipId) ?? null,
  )

  const save = () => {
    if (selected) onOutput(memberOutputFromCandidate(selected))
  }
</script>

<WorkflowStepFrame>
  <SectionTitle
    {title}
    {description}
    {helpModalTextId}
    helpModalTooltip="Hilfe zu diesem Schritt öffnen"
    small={true}
    darkMode={true}
    styleOuter="margin: 0;"
  />
  <WorkflowActionBox label={actionBoxLabel}>
    <p>{roleDescription}</p>
    {#if loading}
      <div class="member-load-state" role="status" aria-live="polite" aria-busy="true">
        <LoadingWrapper
          loading={true}
          darkMode={true}
          progressLabel="Mitglieder der Organisation werden geladen…"
        />
      </div>
    {:else if loadError}
      <p class="member-load-error" role="alert">{loadError}</p>
    {:else if ctx.readOnly && output}
      <dl class="member-summary">
        <div>
          <dt>Mitglied</dt>
          <dd>{output.displayName}</dd>
        </div>
        <div>
          <dt>Anmeldung</dt>
          <dd>{output.username}</dd>
        </div>
      </dl>
    {:else if !available.length}
      <p class="member-load-error" role="alert">{emptyMessage}</p>
    {:else}
      <fieldset class="member-picker">
        <legend>{actionBoxLabel}</legend>
        {#each available as candidate (candidate.membership_id)}
          <label
            class="member-option"
            class:member-option--selected={candidate.membership_id === selectedMembershipId}
          >
            <input
              type="radio"
              name={`demo-reset-${role}-membership`}
              value={candidate.membership_id}
              bind:group={selectedMembershipId}
            />
            <span class="member-option__text">
              <span class="member-option__name">{candidate.display_name}</span>
              <span class="member-option__username">{candidate.username}</span>
            </span>
          </label>
        {/each}
      </fieldset>
      <div class="member-picker__actions">
        <CTA type="primaryButton" content="Weiter" onClick={save} disabled={!selected} />
      </div>
    {/if}
    {#snippet actions()}
      {#if !loading && loadError && onRetry}
        <CTA type="secondaryButton" content="Erneut versuchen" onClick={onRetry} />
      {:else if ctx.readOnly && output}
        <CTA type="secondaryButton" content="Auswahl ändern" onClick={onEdit} />
      {/if}
    {/snippet}
  </WorkflowActionBox>
</WorkflowStepFrame>

<style lang="less">
  .member-load-state {
    min-height: 12.5rem;
  }

  .member-load-error {
    min-height: 4rem;
    color: var(--danger-100, var(--error-100, var(--text-100)));
  }

  .member-picker {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
    gap: 0.6rem;
    margin: 0;
    border: 0;
    padding: 0;
  }

  .member-picker legend {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  /* The web-sdk form theme fixes every label to 60px and hides its direct child
     span (`label > span { opacity: 0 }`); the option restores a native radio and
     visible text like the batch upload duration choice. */
  .member-option {
    display: flex !important;
    flex-direction: row !important;
    justify-content: flex-start !important;
    align-items: center !important;
    gap: 0.7rem;
    width: auto !important;
    min-height: 3.2rem;
    height: auto !important;
    max-height: none !important;
    margin: 0 !important;
    border: 1px solid color-mix(in srgb, currentColor 24%, transparent);
    padding: 0.7rem !important;
    color: inherit !important;
    cursor: pointer;
  }

  .member-option--selected {
    border-color: var(--success-100);
  }

  .member-option > input {
    appearance: auto !important;
    width: 18px !important;
    min-width: 18px !important;
    max-width: 18px !important;
    height: 18px !important;
    flex: 0 0 18px !important;
    margin: 0 !important;
    padding: 0 !important;
    accent-color: var(--kontext-accent, #821e24);
  }

  .member-option__text {
    display: grid !important;
    gap: 0.15rem;
    flex: 1;
    min-width: 0;
    width: auto !important;
    height: auto !important;
    padding: 0 !important;
    color: inherit !important;
    opacity: 1 !important;
    font-size: inherit !important;
    line-height: inherit !important;
    text-transform: none !important;
  }

  .member-option__name,
  .member-option__username {
    width: auto !important;
    height: auto !important;
    padding: 0 !important;
    color: inherit !important;
    opacity: 1 !important;
    text-transform: none !important;
    line-height: 1.35 !important;
    overflow-wrap: anywhere;
  }

  .member-option__name {
    font-size: 1rem !important;
    font-weight: 700 !important;
  }

  .member-option__username {
    font-size: 0.85rem !important;
    font-weight: 400 !important;
  }

  .member-picker__actions {
    display: flex;
    justify-content: flex-end;
  }

  .member-summary {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: 0.9rem;
    margin: 0;
  }

  .member-summary div {
    border-left: 0.2rem solid var(--success-100);
    padding-left: 0.65rem;
  }

  .member-summary dt,
  .member-summary dd {
    margin: 0;
  }

  .member-summary dt {
    font-size: 0.72rem;
    font-weight: 900;
    text-transform: uppercase;
  }

  .member-summary dd {
    overflow-wrap: anywhere;
    margin-top: 0.2rem;
  }
</style>
